<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── BACK ── -->
      <button class="back-btn" @click="goToChapter">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="15 18 9 12 15 6"/></svg>
        <span>Back to {{ cityName || 'Chapter' }}</span>
      </button>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Talk of the Town</span>
        <div class="section-rule"></div>
      </div>
      <div class="posts-header">
        <h2 class="section-title" style="margin-bottom: 0;">{{ cityName }} <em>Discussion</em></h2>
        <button class="new-post-btn" @click="createNewPost">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span>New Post</span>
        </button>
      </div>

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Gathering the conversation…</p>
      </div>

      <!-- ── POSTS ── -->
      <div v-else-if="posts.length" class="posts-feed">
        <div v-for="post in posts" :key="post.id" class="post-card">
          <div class="post-header">
            <div class="post-avatar">
              <img v-if="post.userProfileImageURL" :src="post.userProfileImageURL" :alt="post.userName" />
              <span v-else>{{ (post.userName || '?').charAt(0) }}</span>
            </div>
            <div class="post-meta">
              <p class="post-author">{{ post.userName }}</p>
              <p class="post-time">{{ formatTimestamp(post.timestamp) }}</p>
            </div>
            <div v-if="post.uid === userUID" class="menu-wrap">
              <button class="menu-btn" @click="openMenuId = openMenuId === post.id ? null : post.id">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
              </button>
              <div v-if="openMenuId === post.id" class="menu-dropdown">
                <button class="menu-item" @click="startEdit(post)">Edit Post</button>
                <button class="menu-item menu-item-danger" @click="removePost(post)">Delete Post</button>
              </div>
            </div>
            <div v-else class="menu-wrap">
              <button class="menu-btn" @click="openMenuId = openMenuId === post.id ? null : post.id">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
              </button>
              <div v-if="openMenuId === post.id" class="menu-dropdown">
                <button v-if="!reportedPostIds.includes(post.id)" class="menu-item menu-item-danger" @click="reportingPost = post; openMenuId = null">Report Post</button>
                <span v-else class="menu-item menu-item-disabled">Reported ✓</span>
              </div>
            </div>
          </div>

          <div class="post-divider"></div>

          <div class="post-body">
            <template v-if="editingPostId === post.id">
              <textarea v-model="editText" class="edit-textarea" rows="3"></textarea>

              <div v-if="editMediaPreviews.length" class="media-grid">
                <div v-for="(item, idx) in editMediaPreviews" :key="idx" class="media-thumb">
                  <img v-if="item.type === 'image'" :src="item.url" />
                  <video v-else :src="item.url" controls></video>
                  <button class="media-remove-btn" @click="removeEditMediaItem(idx)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              <button class="add-media-btn" @click="$refs['editFileInput' + post.id][0].click()">+ Add Photos/Videos</button>
              <input
                type="file"
                :ref="'editFileInput' + post.id"
                style="display: none;"
                accept="image/*,video/*"
                multiple
                @change="handleEditFileChange"
              />

              <div class="edit-actions">
                <button class="edit-save-btn" :disabled="!editText.trim() || isSavingEdit" @click="saveEdit(post)">
                  {{ isSavingEdit ? 'Saving…' : 'Save' }}
                </button>
                <button class="edit-cancel-btn" @click="cancelEdit">Cancel</button>
              </div>
            </template>
            <template v-else>
              <p class="post-text">{{ post.text }}</p>
              <div v-if="getMedia(post).length" class="media-gallery" :class="{ single: getMedia(post).length === 1 }">
                <div v-for="(item, idx) in getMedia(post)" :key="idx" class="media-gallery-item">
                  <img v-if="item.type === 'image'" :src="item.url" alt="Post image" />
                  <video v-else controls :src="item.url"></video>
                </div>
              </div>
            </template>
          </div>

          <div class="post-divider"></div>

          <div class="post-actions">
            <button class="post-action-btn" @click="toggleLike(post)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" :class="{ liked: post.likes && post.likes.includes(userUID) }">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
              <span>{{ post.likes ? post.likes.length : 0 }}</span>
            </button>
            <button class="post-action-btn" @click="openComments(post)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              <span>{{ post.commentsCount || 0 }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ── EMPTY ── -->
      <div v-else class="empty-state">
        <p class="empty-title">No posts yet</p>
        <p class="empty-sub">Be the first to talk about {{ cityName }}.</p>
      </div>

      <!-- ── COMMENTS SHEET ── -->
      <div v-if="showCommentsSheet" class="overlay" @click.self="showCommentsSheet = false">
        <div class="sheet">
          <div class="sheet-hd">
            <span>Comments for {{ selectedPost?.userName || 'Post' }}</span>
            <button class="sheet-close" @click="showCommentsSheet = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div class="sheet-body">
            <div v-if="comments.length" class="comments-list">
              <div v-for="comment in comments" :key="comment.id" class="comment-row">
                <div class="comment-avatar">
                  <img v-if="comment.userProfileImageURL" :src="comment.userProfileImageURL" :alt="comment.userName" />
                  <span v-else>{{ (comment.userName || '?').charAt(0) }}</span>
                </div>
                <div>
                  <p class="comment-author">{{ comment.userName }}</p>
                  <p class="comment-text">{{ comment.text }}</p>
                </div>
              </div>
            </div>
            <div v-else class="empty-comments">
              <p class="empty-sub">No comments yet.</p>
            </div>

            <textarea v-model="newCommentText" class="comment-input" placeholder="Add a comment…" rows="3"></textarea>
            <button class="comment-submit" :disabled="!newCommentText.trim()" @click="addComment">
              <div class="btn-tick"></div>
              <span>Post Comment</span>
              <div class="btn-tick"></div>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Report reason picker -->
    <div v-if="reportingPost" class="report-overlay" @click.self="reportingPost = null">
      <div class="report-panel">
        <p class="report-title">Report this post</p>
        <button v-for="reason in reportReasons" :key="reason" class="report-reason-btn" @click="submitReport(reason)">
          {{ reason }}
        </button>
        <button class="report-cancel-btn" @click="reportingPost = null">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { normalizeCity } from "@/utils/city";

export default {
  name: "PostsView",
  props: {
    city: String, // URL-encoded chapter city — same param shape as GroupDetailView's `id`
  },

  data() {
    return {
      cityName: "",
      chapterId: null,
      posts: [],
      comments: [],
      selectedPost: null,
      newCommentText: "",
      showCommentsSheet: false,
      reportingPost: null,
      reportedPostIds: [],
      reportReasons: ['Spam', 'Inappropriate content', 'Harassment', 'False information', 'Other'],
      userUID: auth.currentUser?.uid || null,
      isLoading: true,
      openMenuId: null,
      editingPostId: null,
      editText: "",
      editMediaFiles: [], // newly-added Files pending upload
      editMediaPreviews: [], // [{ url, type, isNew }] — existing (remote) + newly added (local blob)
      isSavingEdit: false,
    };
  },

  async mounted() {
    this.cityName = decodeURIComponent(this.city || "");
    await this.fetchChapterAndPosts();
  },

  methods: {
    async fetchChapterAndPosts() {
      this.isLoading = true;
      try {
        const targetKey = normalizeCity(this.cityName);
        const groupsSnap = await getDocs(collection(db, "groups"));
        const matchingIds = groupsSnap.docs
          .filter((d) => d.data().city && normalizeCity(d.data().city) === targetKey)
          .map((d) => d.id);

        if (matchingIds.length === 0) {
          this.chapterId = null;
          this.posts = [];
          return;
        }
        this.chapterId = matchingIds[0];

        const postLists = await Promise.all(
          matchingIds.map((groupId) => getDocs(query(collection(db, "posts"), where("groupID", "==", groupId))))
        );
        this.posts = postLists
          .flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
          .sort((a, b) => this.toMillis(b.timestamp) - this.toMillis(a.timestamp));
      } catch (error) {
        console.error("Error fetching chapter discussion:", error);
      } finally {
        this.isLoading = false;
      }
    },

    toMillis(ts) {
      if (!ts) return 0;
      if (ts.toDate) return ts.toDate().getTime();
      if (ts.seconds) return ts.seconds * 1000;
      return new Date(ts).getTime() || 0;
    },

    formatTimestamp(ts) {
      const ms = this.toMillis(ts);
      return ms ? new Date(ms).toLocaleString() : "";
    },

    async openComments(post) {
      try {
        this.selectedPost = post;
        const q = query(collection(db, "comments"), where("postID", "==", post.id));
        const snap = await getDocs(q);
        this.comments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.showCommentsSheet = true;
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    },

    async addComment() {
      if (!this.newCommentText.trim() || !this.selectedPost) return;
      try {
        const user = auth.currentUser;
        const comment = {
          userID: this.userUID,
          userName: user?.displayName || "Anonymous",
          userProfileImageURL: user?.photoURL || "",
          text: this.newCommentText.trim(),
          timestamp: new Date(),
          postID: this.selectedPost.id,
        };
        await addDoc(collection(db, "comments"), comment);

        const postRef = doc(db, "posts", this.selectedPost.id);
        const updatedCount = (this.selectedPost.commentsCount || 0) + 1;
        await updateDoc(postRef, { commentsCount: updatedCount });

        this.comments.push(comment);
        this.newCommentText = "";
        this.selectedPost.commentsCount = updatedCount;
        const postInList = this.posts.find((p) => p.id === this.selectedPost.id);
        if (postInList) postInList.commentsCount = updatedCount;

        // Notify the post's owner, if we can tell who that is. NOTE: the field name
        // for a chapter post's author isn't confirmed here — this checks both `uid`
        // and `userID` since PostsView doesn't own the post-creation code (that lives
        // in a separate create-post component this file doesn't have visibility into).
        const ownerId = this.selectedPost.uid || this.selectedPost.userID;
        if (ownerId && ownerId !== this.userUID) {
          await addDoc(collection(db, "notifications"), {
            receiverId: ownerId,
            senderId: this.userUID,
            type: "comment",
            postId: this.selectedPost.id,
            postType: "group",
            city: this.cityName,
            message: `${user?.displayName || "A member"} commented on your post in ${this.cityName}`,
            timestamp: new Date(),
            read: false,
          });
        }
      } catch (error) {
        console.error("Error adding comment:", error);
      }
    },

    async toggleLike(post) {
      if (!this.userUID) return;
      try {
        const postRef = doc(db, "posts", post.id);
        const hasLiked = Array.isArray(post.likes) && post.likes.includes(this.userUID);
        await updateDoc(postRef, { likes: hasLiked ? arrayRemove(this.userUID) : arrayUnion(this.userUID) });
        post.likes = hasLiked
          ? (post.likes || []).filter((id) => id !== this.userUID)
          : [...(post.likes || []), this.userUID];
      } catch (error) {
        console.error("Error toggling like:", error);
      }
    },

    getMedia(post) {
      if (Array.isArray(post.media) && post.media.length) return post.media;
      const legacy = [];
      if (post.imageURL) legacy.push({ url: post.imageURL, type: "image" });
      if (post.videoURL) legacy.push({ url: post.videoURL, type: "video" });
      return legacy;
    },

    startEdit(post) {
      this.openMenuId = null;
      this.editingPostId = post.id;
      this.editText = post.text || "";
      this.editMediaFiles = [];
      // Existing remote media shows immediately; new files get appended as picked
      this.editMediaPreviews = this.getMedia(post).map((item) => ({ ...item, isNew: false }));
    },

    cancelEdit() {
      this.editingPostId = null;
      this.editText = "";
      this.editMediaFiles = [];
      this.editMediaPreviews = [];
    },

    handleEditFileChange(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => {
        this.editMediaFiles.push(file);
        this.editMediaPreviews.push({
          url: URL.createObjectURL(file),
          type: file.type.startsWith("image") ? "image" : "video",
          isNew: true,
        });
      });
      event.target.value = "";
    },

    removeEditMediaItem(idx) {
      const item = this.editMediaPreviews[idx];
      if (item.isNew) {
        // Figure out which pending File this preview corresponds to and drop it too
        const newIdxBefore = this.editMediaPreviews.slice(0, idx).filter((p) => p.isNew).length;
        this.editMediaFiles.splice(newIdxBefore, 1);
      }
      this.editMediaPreviews.splice(idx, 1);
    },

    async saveEdit(post) {
      if (!this.editText.trim() || this.isSavingEdit) return;
      this.isSavingEdit = true;
      try {
        // Upload any newly-added files
        const storage = getStorage();
        const uploaded = await Promise.all(
          this.editMediaFiles.map(async (file, idx) => {
            const isImage = file.type.startsWith("image");
            const ext = isImage ? "jpg" : "mp4";
            const fileRef = storageRef(storage, `chapter_posts/${post.id}_edit_${idx}_${Date.now()}.${ext}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            return { url, type: isImage ? "image" : "video" };
          })
        );

        // Keep existing (non-removed) remote items, in order, plus newly uploaded ones
        const kept = this.editMediaPreviews.filter((p) => !p.isNew).map(({ url, type }) => ({ url, type }));
        const media = [...kept, ...uploaded];

        await updateDoc(doc(db, "posts", post.id), {
          text: this.editText.trim(),
          media,
          imageURL: null, // clear legacy single-media fields now that `media` is authoritative
          videoURL: null,
        });

        post.text = this.editText.trim();
        post.media = media;
        post.imageURL = null;
        post.videoURL = null;

        this.cancelEdit();
      } catch (error) {
        console.error("Error saving post edit:", error);
      } finally {
        this.isSavingEdit = false;
      }
    },

    async removePost(post) {
      this.openMenuId = null;
      if (!window.confirm("Delete this post?")) return;
      try {
        await deleteDoc(doc(db, "posts", post.id));
        this.posts = this.posts.filter((p) => p.id !== post.id);
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    },

    // Same flags collection and shape as ExploreView.vue's handleReportPost
    // — postType distinguishes which admin queue section it belongs to,
    // and a content snapshot is captured so the report is still readable
    // even if the post itself is deleted afterward.
    async submitReport(reason) {
      const post = this.reportingPost;
      this.reportingPost = null;
      if (!post || !auth.currentUser) return;
      try {
        await addDoc(collection(db, "flags"), {
          postId: post.id,
          postType: "chapter",
          chapterId: this.chapterId || null,
          postOwnerUid: post.uid || null,
          reporterUid: auth.currentUser.uid,
          reason,
          status: "pending",
          createdAt: serverTimestamp(),
          postSnapshot: {
            text: post.text || "",
            city: this.cityName || "",
            image: (this.getMedia(post)[0] && this.getMedia(post)[0].url) || null,
            fullName: post.userName || "",
          },
        });
        this.reportedPostIds.push(post.id);
      } catch (error) {
        console.error("Error submitting report:", error);
      }
    },

    async createNewPost() {
      if (!this.chapterId) {
        // No chapter doc exists yet for this city — create one so the post has somewhere to attach
        try {
          const docRef = await addDoc(collection(db, "groups"), {
            city: this.cityName,
            members: this.userUID ? [this.userUID] : [],
            createdAt: new Date().toISOString(),
          });
          this.chapterId = docRef.id;
        } catch (error) {
          console.error("Error creating chapter for new post:", error);
          return;
        }
      }
      this.$router.push(`/group/${this.chapterId}/create-post`);
    },

    goToChapter() {
      this.$router.push(`/groups/${encodeURIComponent(this.cityName)}`);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 680px; margin: 0 auto; padding: 48px 24px 100px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── BACK ── */
.back-btn { display: flex; align-items: center; gap: 8px; background: none; border: none; color: rgba(210,175,100,0.45); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; margin-bottom: 32px; padding: 0; transition: color 0.2s; }
.back-btn:hover { color: rgba(210,175,100,0.8); }
.back-btn svg { width: 16px; height: 16px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 34px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.posts-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 36px; }
.new-post-btn { display: flex; align-items: center; gap: 8px; background: transparent; border: 1px solid rgba(210,175,100,0.3); padding: 12px 22px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.7); transition: border-color 0.2s, color 0.2s; }
.new-post-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,1); }
.new-post-btn svg { width: 14px; height: 14px; }

/* ── LOADING / EMPTY ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); text-align: center; }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── POSTS ── */
.posts-feed { display: flex; flex-direction: column; gap: 24px; }
.post-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); position: relative; }
.post-header { display: flex; align-items: center; gap: 14px; padding: 18px 22px; }
.post-meta { flex: 1; min-width: 0; }
.post-avatar { width: 40px; height: 40px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(210,175,100,0.2); background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 18px; font-weight: 300; color: rgba(210,175,100,0.5); flex-shrink: 0; }
.post-avatar img { width: 100%; height: 100%; object-fit: cover; }

/* ── OWNER MENU ── */
.menu-wrap { position: relative; flex-shrink: 0; }
.menu-btn { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.2); padding: 4px; display: flex; align-items: center; transition: color 0.2s; }
.menu-btn:hover { color: rgba(210,175,100,0.6); }
.menu-btn svg { width: 16px; height: 16px; }
.menu-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #0f0f10; border: 1px solid rgba(210,175,100,0.2); min-width: 150px; z-index: 50; }
.menu-item { display: block; width: 100%; padding: 12px 16px; background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.4); text-align: left; transition: color 0.2s, background 0.2s; }
.menu-item:hover { background: rgba(210,175,100,0.05); color: rgba(237,232,223,0.8); }
.menu-item-danger { color: rgba(180,70,70,0.5); }
.menu-item-danger:hover { color: rgba(200,80,80,0.9); background: rgba(200,80,80,0.04); }
.menu-item-disabled { display: block; padding: 10px 14px; font-size: 12.5px; color: rgba(130,190,140,0.6); }

/* ── REPORT PANEL ── */
.report-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 24px; }
.report-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 340px; padding: 24px; }
.report-title { font-family: 'Cormorant', serif; font-size: 19px; color: #edeae2; margin-bottom: 16px; }
.report-reason-btn { display: block; width: 100%; text-align: left; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); color: rgba(237,232,223,0.6); font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; }
.report-reason-btn:hover { border-color: rgba(200,80,80,0.35); color: rgba(200,80,80,0.85); }
.report-cancel-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: rgba(237,232,223,0.3); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px; margin-top: 6px; cursor: pointer; }

/* ── INLINE EDIT ── */
.edit-textarea { width: 100%; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.25); padding: 12px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); resize: vertical; margin-bottom: 12px; }
.edit-textarea:focus { border-color: rgba(210,175,100,0.5); }
.edit-actions { display: flex; gap: 10px; }
.edit-save-btn { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5); padding: 9px 18px; cursor: pointer; transition: background 0.2s, opacity 0.2s; }
.edit-save-btn:hover:not(:disabled) { background: rgba(210,175,100,0.16); }
.edit-save-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.edit-cancel-btn { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.3); background: none; border: none; cursor: pointer; padding: 9px 6px; transition: color 0.2s; }
.edit-cancel-btn:hover { color: rgba(237,232,223,0.6); }
.post-author { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; margin-bottom: 2px; }
.post-time { font-size: 9px; font-weight: 500; letter-spacing: 2px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.post-divider { height: 1px; background: rgba(210,175,100,0.08); }

.post-body { padding: 20px 22px; }
.post-text { font-size: 14px; font-weight: 300; color: rgba(237,232,223,0.7); line-height: 1.8; letter-spacing: 0.2px; margin-bottom: 16px; }
.post-text:last-child { margin-bottom: 0; }
.media-gallery { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px; margin-top: 8px; }
.media-gallery.single { grid-template-columns: 1fr; }
.media-gallery-item { position: relative; aspect-ratio: 1; overflow: hidden; }
.media-gallery.single .media-gallery-item { aspect-ratio: 16/10; }
.media-gallery-item img, .media-gallery-item video { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.8); }

.media-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
.media-thumb { position: relative; aspect-ratio: 1; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; }
.media-thumb img, .media-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
.media-remove-btn { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(210,175,100,0.3); color: rgba(237,232,223,0.8); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s, color 0.2s; }
.media-remove-btn:hover { background: rgba(200,60,60,0.7); color: #fff; }
.media-remove-btn svg { width: 11px; height: 11px; }
.add-media-btn { display: inline-block; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.6); background: transparent; border: 1px solid rgba(210,175,100,0.25); padding: 8px 14px; cursor: pointer; margin-bottom: 12px; transition: border-color 0.2s, color 0.2s; }
.add-media-btn:hover { border-color: rgba(210,175,100,0.55); color: rgba(210,175,100,0.9); }

.post-actions { display: flex; align-items: center; gap: 0; }
.post-action-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; background: transparent; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.2); transition: color 0.2s; }
.post-action-btn:hover { color: rgba(210,175,100,0.6); }
.post-action-btn + .post-action-btn { border-left: 1px solid rgba(210,175,100,0.08); }
.post-action-btn svg { width: 16px; height: 16px; transition: stroke 0.2s; }
.post-action-btn svg.liked { stroke: rgba(210,175,100,0.9); fill: rgba(210,175,100,0.25); }

/* ── COMMENTS SHEET ── */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.82); z-index: 100; display: flex; align-items: flex-end; justify-content: center; }
.sheet { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.18); border-bottom: none; width: 100%; max-width: 560px; max-height: 78vh; display: flex; flex-direction: column; }
.sheet-hd { display: flex; align-items: center; justify-content: space-between; padding: 20px 22px; border-bottom: 1px solid rgba(210,175,100,0.1); font-family: 'Cormorant', serif; font-size: 18px; color: #edeae2; }
.sheet-close { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.3); display: flex; transition: color 0.2s; }
.sheet-close:hover { color: rgba(210,175,100,0.7); }
.sheet-close svg { width: 16px; height: 16px; }
.sheet-body { padding: 20px 22px; overflow-y: auto; display: flex; flex-direction: column; gap: 18px; }
.comments-list { display: flex; flex-direction: column; gap: 16px; }
.comment-row { display: flex; align-items: flex-start; gap: 12px; }
.comment-avatar { width: 32px; height: 32px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(210,175,100,0.2); background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 13px; color: rgba(210,175,100,0.5); flex-shrink: 0; }
.comment-avatar img { width: 100%; height: 100%; object-fit: cover; }
.comment-author { font-family: 'Cormorant', serif; font-size: 14px; color: #edeae2; margin-bottom: 3px; }
.comment-text { font-size: 12.5px; font-weight: 300; color: rgba(237,232,223,0.55); line-height: 1.6; }
.empty-comments { padding: 12px 0; }
.comment-input { width: 100%; background: transparent; border: 1px solid rgba(210,175,100,0.15); padding: 12px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); resize: vertical; transition: border-color 0.2s; }
.comment-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.comment-input:focus { border-color: rgba(210,175,100,0.4); }
.comment-submit { align-self: flex-end; display: flex; align-items: center; gap: 10px; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5); padding: 11px 20px; cursor: pointer; transition: background 0.2s, opacity 0.2s; }
.comment-submit:hover:not(:disabled) { background: rgba(210,175,100,0.16); }
.comment-submit:disabled { opacity: 0.3; cursor: not-allowed; }
.btn-tick { width: 12px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }

/* ── RESPONSIVE ── */
@media (max-width: 600px) { .page { padding: 32px 16px 80px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 28px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>